<template>
  <div class="part-view">
    <!-- Loading state for files -->
    <div v-if="filesQuery.isLoading.value">
      <v-skeleton-loader type="image" height="400" class="rounded" />
    </div>

    <!-- Error state for files -->
    <v-alert
      v-else-if="filesQuery.isError.value"
      type="warning"
      variant="tonal"
    >
      Unable to load files for this part.
    </v-alert>

    <!-- Carousel with files -->
    <v-carousel
      v-else-if="carouselItems.length > 0"
      hide-delimiters
      :show-arrows="carouselItems.length > 1"
      height="auto"
    >
      <v-carousel-item v-for="(item, index) in carouselItems" :key="index">
        <div class="d-flex justify-center align-center h-100">
          <v-img
            v-if="item.type === 'image'"
            :src="item.url"
            :alt="item.title"
            contain
            max-height="400"
            class="rounded"
          />
          <v-card
            v-else
            variant="outlined"
            class="pa-4 d-flex flex-column align-center justify-center"
            style="height: 300px; width: 300px"
          >
            <v-icon size="64" color="primary" class="mb-4">
              {{ getFileIcon(item.mimetype) }}
            </v-icon>
            <div class="text-h6 text-center mb-2">{{ item.title }}</div>
            <div class="text-caption text-medium-emphasis text-center">
              {{ item.categoryLabel }}
            </div>
            <v-btn
              v-if="item.url"
              :href="item.url"
              target="_blank"
              color="primary"
              variant="outlined"
              class="mt-4"
            >
              <v-icon start>mdi-open-in-new</v-icon>
              View File
            </v-btn>
          </v-card>
        </div>
      </v-carousel-item>
    </v-carousel>

    <!-- No files message -->
    <v-alert v-else type="info" variant="tonal">
      No files available for this part.
    </v-alert>

    <!-- Title + condition chip -->
    <div class="d-flex align-center justify-space-between mt-4 gap-3">
      <span class="text-title-large">{{ part.part_type || "—" }}</span>
      <v-chip
        v-if="part.condition"
        size="small"
        variant="tonal"
        :color="chipColorForCondition(part.condition)"
        class="font-weight-medium"
      >
        {{ toTitleCase(part.condition) }}
      </v-chip>
    </div>

    <!-- Description -->
    <p v-if="part.description" class="mt-2 text-body-1">
      {{ part.description }}
    </p>

    <!-- TRACE table -->
    <v-table class="mt-4 part-view-table">
      <tbody>
        <tr>
          <th colspan="2" class="text-center bg-grey-lighten-2">TRACE</th>
        </tr>
        <tr v-if="part.manufacturer">
          <td class="font-weight-medium">{{ t(strings.manufacturerLabel) }}</td>
          <td>{{ part.manufacturer }}</td>
        </tr>
        <tr v-if="part.part_type">
          <td class="font-weight-medium">{{ t(strings.partTypeLabel) }}</td>
          <td>{{ part.part_type }}</td>
        </tr>
        <tr v-if="part.turbine_model">
          <td class="font-weight-medium">{{ t(strings.turbineModelLabel) }}</td>
          <td>{{ part.turbine_model }}</td>
        </tr>
        <tr v-if="part.frame">
          <td class="font-weight-medium">{{ t(strings.frameLabel) }}</td>
          <td>{{ part.frame }}</td>
        </tr>
        <tr v-if="part.casting_number">
          <td class="font-weight-medium">
            {{ t(strings.castingNumberLabel) }}
          </td>
          <td>{{ part.casting_number }}</td>
        </tr>
        <tr v-if="part.part_number">
          <td class="font-weight-medium">{{ t(strings.partNumberLabel) }}</td>
          <td>{{ part.part_number }}</td>
        </tr>
        <tr v-if="part.serial_number">
          <td class="font-weight-medium">{{ t(strings.serialNumberLabel) }}</td>
          <td>{{ part.serial_number }}</td>
        </tr>
      </tbody>
    </v-table>

    <!-- CONDITION table -->
    <v-table class="mt-4 part-view-table">
      <tbody>
        <tr>
          <th colspan="2" class="text-center bg-grey-lighten-2">CONDITION</th>
        </tr>
        <tr v-if="part.condition">
          <td class="font-weight-medium">{{ t(strings.conditionLabel) }}</td>
          <td>{{ part.condition }}</td>
        </tr>
        <tr v-if="part.date_listed">
          <td class="font-weight-medium">{{ t(strings.dateListedLabel) }}</td>
          <td>{{ formatDate(part.date_listed) }}</td>
        </tr>
        <tr v-if="part.date_sold">
          <td class="font-weight-medium">{{ t(strings.dateSoldLabel) }}</td>
          <td>{{ formatDate(part.date_sold) }}</td>
        </tr>
        <tr v-if="part.tags && part.tags.length > 0">
          <td class="font-weight-medium">{{ t(strings.tagsLabel) }}</td>
          <td>
            <div class="d-flex flex-wrap gap-1 justify-end">
              <v-chip
                v-for="tag in part.tags"
                :key="tag"
                size="small"
                variant="tonal"
                color="primary"
              >
                {{ tag }}
              </v-chip>
            </div>
          </td>
        </tr>
        <tr v-if="part.address">
          <td class="font-weight-medium">{{ t(strings.addressLabel) }}</td>
          <td><AddressDisplay :address="part.address" /></td>
        </tr>
        <tr v-if="part.upgrades">
          <td class="font-weight-medium">
            {{ t(strings.upgradeDesignationsLabel) }}
          </td>
          <td>{{ part.upgrades }}</td>
        </tr>
        <tr v-if="part.total_factored_fired_hours != null">
          <td class="font-weight-medium">
            {{ t(strings.totalFactoredFiredHoursLabel) }}
          </td>
          <td>{{ part.total_factored_fired_hours }}</td>
        </tr>
        <tr v-if="part.total_factored_fired_starts != null">
          <td class="font-weight-medium">
            {{ t(strings.totalFactoredFiredStartsLabel) }}
          </td>
          <td>{{ part.total_factored_fired_starts }}</td>
        </tr>
        <tr v-if="part.times_refurbished != null">
          <td class="font-weight-medium">
            {{ t(strings.timesRefurbishedLabel) }}
          </td>
          <td>{{ part.times_refurbished }}</td>
        </tr>
        <tr v-if="part.emergency_trips != null">
          <td class="font-weight-medium">
            {{ t(strings.emergencyTripsLabel) }}
          </td>
          <td>{{ part.emergency_trips }}</td>
        </tr>
      </tbody>
    </v-table>

    <!-- LISTING table -->
    <v-table class="mt-4 part-view-table">
      <tbody>
        <tr>
          <th colspan="2" class="text-center bg-grey-lighten-2">LISTING</th>
        </tr>
        <tr v-if="part.status">
          <td class="font-weight-medium">{{ t(strings.statusLabel) }}</td>
          <td>{{ part.status }}</td>
        </tr>
        <tr v-if="part.quantity != null">
          <td class="font-weight-medium">{{ t(strings.quantityLabel) }}</td>
          <td>{{ part.quantity }}</td>
        </tr>
        <tr v-if="part.matched_hardware != null">
          <td class="font-weight-medium">
            {{ t(strings.matchedHardwareLabel) }}
          </td>
          <td>
            <v-chip
              :color="part.matched_hardware ? 'success' : 'default'"
              size="small"
              variant="tonal"
            >
              {{
                part.matched_hardware
                  ? t(strings.available)
                  : t(strings.notAvailable)
              }}
            </v-chip>
          </td>
        </tr>
        <tr v-if="part.updated_at">
          <td class="font-weight-medium">{{ t(strings.updatedLabel) }}</td>
          <td>{{ formatDate(part.updated_at) }}</td>
        </tr>
      </tbody>
    </v-table>

    <!-- FILES table -->
    <v-table class="mt-4 part-view-table">
      <tbody>
        <tr>
          <th colspan="2" class="text-center bg-grey-lighten-2">FILES</th>
        </tr>
        <template v-for="category in fileCategories" :key="category.key">
          <tr v-if="getFileCount(part, category.key) > 0">
            <td class="font-weight-medium">{{ t(category.labelKey) }}</td>
            <td>
              {{ getFileCount(part, category.key) }}
              {{
                getFileCount(part, category.key) === 1
                  ? t(strings.file)
                  : t(strings.files)
              }}
            </td>
          </tr>
        </template>

        <!-- Raw Data Section for Admin Users -->
        <template v-if="isAdmin && part.raw_data">
          <tr>
            <th colspan="2"></th>
          </tr>
          <tr>
            <th colspan="2" class="text-center bg-grey-lighten-2">
              {{ t(strings.rawDataLabel) }}
            </th>
          </tr>
          <tr v-for="(value, key) in part.raw_data" :key="key">
            <td class="font-weight-medium">{{ key }}</td>
            <td>{{ value }}</td>
          </tr>
        </template>
      </tbody>
    </v-table>
  </div>
</template>

<script setup lang="ts">
import { part_view_strings as strings } from "./PartView.strings.ts";
import { useReverseT } from "../../i18n.ts";
import type { Part, PartCondition, PartFile } from "@vendata/power-up-spec";
import { formatDate } from "../../lib/index.ts";
import AddressDisplay from "../../components/AddressDisplay.vue";
import { computed, ref, watch } from "vue";
import { useGetPartFiles } from "../../requests/parts/index.ts";
import { isAdminFromSession } from "@vendata/power-up-clients-common";
import { useKratosSession } from "@saflib/ory-kratos-sdk";

const sessionQuery = useKratosSession();
const session = sessionQuery.data.value;
const isAdmin = computed(() => (session ? isAdminFromSession(session) : false));

const { t } = useReverseT();

const props = defineProps<{
  part: Part;
}>();

const fileCategories = computed(() => [
  { key: "pedigree", labelKey: strings.pedigreeFilesLabel },
  { key: "shop_release", labelKey: strings.shopReleaseFilesLabel },
  { key: "flow_test_report", labelKey: strings.flowTestFilesLabel },
  {
    key: "dimensional_inspection",
    labelKey: strings.dimensionalInspectionFilesLabel,
  },
  { key: "nde_report", labelKey: strings.ndeReportFilesLabel },
  { key: "photo", labelKey: strings.photoFilesLabel },
]);

const getFileCount = (part: Part, category: string) => {
  return part.file_summary?.[category] || 0;
};

const chipColorForCondition = (condition: PartCondition) => {
  if (condition === "new") return "success";
  if (condition === "repairable") return "info";
  if (condition === "repaired") return "warning";
  if (condition === "as removed") return "grey";
  if (condition === "inspected") return "purple";
  if (condition === "scrap") return "amber-darken-4";
  return "info";
};

const toTitleCase = (condition: PartCondition) => {
  return condition
    ?.replace("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const partIdRef = ref(props.part?.id || "");
const filesQuery = useGetPartFiles({ partId: partIdRef });
const existingFiles = ref<PartFile[]>([]);

watch(
  () => props.part?.id,
  (newPartId) => {
    partIdRef.value = newPartId || "";
  },
  { immediate: true },
);

watch(
  () => filesQuery.data.value,
  (newFiles) => {
    if (newFiles) {
      existingFiles.value = newFiles;
    }
  },
  { immediate: true },
);

const fileCategoryConfig = {
  pedigree: { label: "Pedigree Files" },
  shop_release: { label: "Shop Release Files" },
  flow_test_report: { label: "Flow Test Files" },
  dimensional_inspection: { label: "Dimensional Inspection Files" },
  nde_report: { label: "NDE Report Files" },
  photo: { label: "Photo Files" },
};

const carouselItems = computed(() => {
  const items: Array<{
    type: "image" | "file";
    url: string;
    title: string;
    mimetype: string;
    categoryLabel: string;
  }> = [];

  const photoFiles = existingFiles.value.filter(
    (file) => file.category === "photo" && file.url,
  );
  photoFiles.forEach((file) => {
    items.push({
      type: "image",
      url: file.url!,
      title: file.file_original_name,
      mimetype: file.mimetype,
      categoryLabel: fileCategoryConfig.photo.label,
    });
  });

  if (photoFiles.length === 0 && props.part.thumbnail_url) {
    items.push({
      type: "image",
      url: props.part.thumbnail_url,
      title: props.part.part_number,
      mimetype: "image/jpeg",
      categoryLabel: fileCategoryConfig.photo.label,
    });
  }

  if (!existingFiles.value.length && items.length === 0) return [];

  const otherCategories = [
    "pedigree",
    "shop_release",
    "flow_test_report",
    "dimensional_inspection",
    "nde_report",
  ];
  otherCategories.forEach((category) => {
    const categoryFiles = existingFiles.value.filter(
      (file) => file.category === category && file.url,
    );
    categoryFiles.forEach((file) => {
      items.push({
        type: file.mimetype.includes("image") ? "image" : "file",
        url: file.url!,
        title: file.file_original_name,
        mimetype: file.mimetype,
        categoryLabel:
          fileCategoryConfig[category as keyof typeof fileCategoryConfig].label,
      });
    });
  });

  return items;
});

const getFileIcon = (mimetype: string) => {
  if (mimetype.includes("pdf")) return "mdi-file-pdf-box";
  if (mimetype.includes("word") || mimetype.includes("document"))
    return "mdi-file-word-box";
  if (mimetype.includes("image")) return "mdi-file-image";
  if (mimetype.includes("text")) return "mdi-file-document-outline";
  return "mdi-file-outline";
};

import { eventLogger } from "@vendata/power-up-clients-common";
import { onMounted } from "vue";
import { useClientCommon } from "@saflib/vue";
import { traceFromPart } from "@vendata/power-up-clients-common";

onMounted(() => {
  eventLogger.emitProductEvent({
    ...useClientCommon("PartView"),
    event: "part_detail_view",
    context: {
      ...traceFromPart(props.part),
    },
  });
});
</script>

<style scoped>
.part-view-table tbody td:last-child {
  text-align: right;
}
</style>
